import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { AtsCreatedApplicationsfromApplicationsFragment } from 'generated/resources/graphql'
import sortBy from 'lodash/sortBy'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useApplicationPhases } from 'modules/ats/hooks/useApplicationPhases'
import { Jobs } from 'modules/ats/store/collections/jobs'

import { useAddApplicationsToJobPosting } from './useAddApplicationsToJobPosting'

interface Props {
  applicationIds: string[]
  onClose: () => void
  onSuccess: (applications: AtsCreatedApplicationsfromApplicationsFragment[]) => void
}

export const AddApplicationsToJobPostingModal = ({ applicationIds, onSuccess, onClose }: Props) => {
  const params = useParams()
  const activeJobs = useLazyRef(() => new Jobs()).current
  const { loading: jobsLoading } = useAsyncData(async () => {
    await activeJobs.fetch({ data: { statuses: ['published', 'unlisted'] } })
  }, [])
  const [selectedJobOpeningId, setSelectedJobOpeningId] = React.useState<string>()
  const { loading: adding, addApplicationsToJobPosting } = useAddApplicationsToJobPosting()
  const { loading: loadingPhases, applicationPhases } = useApplicationPhases({
    variables: { atsJobPostingId: selectedJobOpeningId },
  })
  const [selectedPhaseId, setSelectedPhaseId] = React.useState<string>()
  const jobId = String(params.jobId)
  const jobsToShow = activeJobs.models.filter((job) => job.id !== jobId)
  const publishedJobsOptions = sortBy(jobsToShow, (job) => job.attributes.title).map((job) => ({
    label: job.attributes.title,
    value: job.id,
  }))
  const phaseOptions = selectedJobOpeningId
    ? sortBy(applicationPhases, (phase) => phase.position).map((phase) => ({
        label: phase.name,
        value: phase.id,
      }))
    : []

  const setSelectedPhase = (phaseId: string | undefined) => {
    const selected = phaseId ? String(phaseId) : undefined
    setSelectedPhaseId(selected)
  }

  const onSubmit = async () => {
    if (!selectedJobOpeningId) {
      return
    }

    const response = await addApplicationsToJobPosting({
      ids: applicationIds,
      atsJobPostingId: selectedJobOpeningId,
      atsApplicationPhaseId: selectedPhaseId,
    })

    const result = handleResult(response, () => undefined)

    if (result.success) {
      const applications = result.data.applications ?? []

      const message =
        applications.length === applicationIds.length
          ? i18n.t('ats.candidates.applications.bulk_add_to_job_opening.modal.success_message')
          : i18n.tp(
              'ats.candidates.applications.bulk_add_to_job_opening.modal.existing_applications',
              {
                count: applicationIds.length - applications.length,
              }
            )

      app.setFlash({
        type: 'correct',
        message,
      })

      onSuccess(applications)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.candidates.applications.bulk_add_to_job_opening.modal.error_message'),
      })
    }
  }

  return (
    <Modal
      illustration="add-user"
      title={i18n.tp('ats.candidates.applications.bulk_add_to_job_opening.modal.title', {
        count: applicationIds.length,
      })}
      description={i18n.t('ats.candidates.applications.bulk_add_to_job_opening.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={!selectedJobOpeningId || adding} submit>
          {i18n.t('ats.candidates.applications.bulk_add_to_job_opening.modal.submit')}
        </Button>
      }
    >
      <Box gap="s16">
        <Select
          label={i18n.t('ats.candidates.applications.bulk_add_to_job_opening.modal.job_opening')}
          none={i18n.t(
            'ats.candidates.applications.bulk_add_to_job_opening.modal.job_select_prompt'
          )}
          options={publishedJobsOptions}
          onChange={(job) => setSelectedJobOpeningId(String(job))}
          readonly={jobsLoading || !publishedJobsOptions.length || adding}
          mandatory
        />
        <Select
          label={i18n.t('ats.candidates.applications.bulk_add_to_job_opening.modal.phase')}
          none={i18n.t(
            'ats.candidates.applications.bulk_add_to_job_opening.modal.phase_select_prompt'
          )}
          options={phaseOptions}
          onChange={(phase) => setSelectedPhase(phase)}
          readonly={!phaseOptions.length || loadingPhases || adding}
        />
      </Box>
    </Modal>
  )
}
